import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { toast } from "react-toastify";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useWebSocket } from "../contexts/WebSocketContext";
import { updateShipmentDeliveryType, deleteShipment } from "../utils/api";
// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const containerStyle = {
  width: "100%",
  height: "100%",
  position: "relative",
  zIndex: 1, // Ensure map doesn't overlap with modal
};

// Center of Netherlands as default
const defaultCenter = [52.1326, 5.2913];

// Marker colors based on delivery partner
const markerColors = {
  NP: "#FF0000",
  GLS: "#0000FF",
  "Self Pick Up": "#000000",
  "np_pickup": "#00AA00", // Renamed from "pickup"
  "gls_pickup": "#0096FF", // Renamed from "GLS pickup"
  default: "#9932CC",
};


// This component does nothing - just a placeholder replacement for MapUpdater
// It won't change the zoom or bounds at all
function NoZoomUpdater() {
  return null;
}

// This component sets the loading state to false when the map is ready
function MapReadyHandler({ onMapReady }) {
  const hasCalledRef = useRef(false);

  // Call onMapReady exactly once, immediately
  if (!hasCalledRef.current) {
    console.log("Map is fully initialized and ready");
    // Use setTimeout to avoid triggering during render
    setTimeout(() => {
      onMapReady();
    }, 0);
    hasCalledRef.current = true;
  }

  return null;
}

// This component sets the marker ref and handles the popup opening
function MarkerWithPopup({ shipment, icon, isSelected, handlers }) {
  const markerRef = useRef(null);
  const map = useMap();
  const [selectedTimestamp, setSelectedTimestamp] = useState(null);

  // Open popup when selected from search
  useEffect(() => {
    if (isSelected && markerRef.current) {
      // Create a new timestamp whenever the shipment is selected
      // This ensures the effect runs even if the same shipment is selected multiple times
      setSelectedTimestamp(Date.now());
      
      // Open popup
      markerRef.current.openPopup();
      
      // Pan map to this marker
      map.setView(
        [shipment.latitude, shipment.longitude],
        map.getZoom() || 12
      );
    }
  }, [isSelected, shipment, map]);

  // This additional effect ensures the popup reopens if the component is already
  // mounted and the user searches for the same shipment again
  useEffect(() => {
    // This will run whenever selectedTimestamp changes (i.e., a new selection)
    if (selectedTimestamp && markerRef.current) {
      // Reopen the popup
      setTimeout(() => {
        if (markerRef.current) {
          markerRef.current.openPopup();
        }
      }, 50); // Small delay to ensure the map has time to process
    }
  }, [selectedTimestamp]);

  return (
    <Marker
      ref={markerRef}
      key={shipment.number}
      position={[shipment.latitude, shipment.longitude]}
      icon={icon}
    >
      <Popup>
        <div className="space-y-2">
          <strong className="block text-lg mb-2">
            Delivery {shipment.number}
          </strong>
          <div className="space-y-1">
            <div>
              <span className="font-medium">Address:</span>{" "}
              {shipment.address}
            </div>
            <div>
              <span className="font-medium">Delivery Type:</span>{" "}
              {shipment.delivery_partner}
            </div>
            <div>
              <span className="font-medium">Customer Name:</span>{" "}
              {shipment?.recipient?.name}
            </div>
            <div>
              <span className="font-medium">Customer Phone Number:</span>{" "}
              {shipment?.recipient?.phone}
            </div>
          </div>
          <div className="mt-3 flex justify-end space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlers.handleDeleteShipment(shipment);
              }}
              className="px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Delete Shipment
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlers.handleChangeDeliveryType(shipment);
              }}
              className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Change Delivery Type
            </button>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

// Component that gives map reference to parent
function MapRefProvider({ onMapReady }) {
  const map = useMap();
  
  useEffect(() => {
    if (map && onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);
  
  return null;
}

//SHES1703797135
const ShipmentsMap = ({
  shipments = [],
  onStatsUpdate,
  selectedDate,
  onShipmentsUpdate,
  selectedShipment,
  onMapReady,
}) => {

  // Generate a hash key based on shipment IDs to force re-render when shipments change
  const shipmentsKey = useMemo(() => {
    const ids =
      shipments
        ?.map((s) => s.id)
        ?.sort()
        ?.join(",") || "";
    console.log("Generated shipments key based on IDs:", ids);
    return `map-${ids}`;
  }, [shipments]);

  console.log("ShipmentsMap rendering with", shipments?.length, "shipments");
  console.log("Current shipments key:", shipmentsKey);

  // Group shipments by type and location for self-pickups
  const { deliveryShipments, selfPickupLocations, stats } = useMemo(() => {
    const deliveries = [];
    const selfPickups = new Map();
    const npDeliveries = [];
    const partnerDeliveries = [];
    const selfSubmittedDeliveries = [];
    const glsPickupDeliveries = [];
    const otherDeliveries = [];

    // Process all shipments, excluding deleted ones
    shipments?.forEach((shipment) => {
      switch (shipment.delivery_partner) {
        case "self pick up":
          const key = `${shipment.latitude},${shipment.longitude}`;
          if (!selfPickups.has(key)) {
            selfPickups.set(key, {
              latitude: shipment.latitude,
              longitude: shipment.longitude,
              address: shipment.address,
              count: 1,
            });
          } else {
            const location = selfPickups.get(key);
            location.count += 1;
            selfPickups.set(key, location);
          }
          break;
        case "NP":
          npDeliveries.push(shipment);
          deliveries.push(shipment);
          break;
        case "GLS":
          partnerDeliveries.push(shipment);
          deliveries.push(shipment);
          break;
        case "gls_pickup": // renamed
          glsPickupDeliveries.push(shipment);
          deliveries.push(shipment);
          break;
        case "np_pickup": // renamed
          selfSubmittedDeliveries.push(shipment);
          deliveries.push(shipment);
          break;
        default:
          otherDeliveries.push(shipment);
          deliveries.push(shipment);
          break;
      }
    });    

    const selfPickupCount = shipments.filter(
      (s) =>
        s.delivery_partner === "Self Pick Up"
    ).length;

    // Calculate statistics
    const stats = {
      totalShipments: shipments.length,
      npDeliveries: npDeliveries.length,
      partnerDeliveries: partnerDeliveries.length,
      selfSubmittedDeliveries: selfSubmittedDeliveries.length,
      glsPickupDeliveries: glsPickupDeliveries.length,
      otherDeliveries: otherDeliveries.length,
      selfPickupLocations: selfPickups.size,
      totalSelfPickups: selfPickupCount,
    };

    return {
      deliveryShipments: deliveries,
      selfPickupLocations: Array.from(selfPickups.values()),
      stats,
    };
  }, [shipments]);

  // Notify parent component about updated stats using useEffect
  useEffect(() => {
    if (onStatsUpdate && stats) {
      onStatsUpdate(stats);
    }
  }, [stats, onStatsUpdate]);

  // Custom icon based on partner type
  const createCustomIcon = (type) => {
    // Use the specified color if it exists, otherwise use the default color
    const color = markerColors[type] || markerColors.default;
    return L.divIcon({
      className: "custom-icon",
      html: `<div style="
        background-color: ${color};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 0 4px rgba(0,0,0,0.4);
      "></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
  };

  const handleDeleteShipment = async (shipment) => {
    try {
      // Format the date to YYYY-MM-DD
      const formattedDate =
        selectedDate instanceof Date
          ? selectedDate.toISOString().split("T")[0]
          : selectedDate;

      console.log("Deleting shipment:", {
        shipmentId: shipment.id,
        date: formattedDate,
        url: `${process.env.REACT_APP_PYTHON_BACKEND_URL}/api/remove_shipments?shipment_number=${shipment.id}&delivery_date=${formattedDate}`,
      });

      const deleteResult = await deleteShipment(shipment.number);

      if (!deleteResult?.success) {
        throw new Error(`Failed to delete shipment: ${deleteResult?.error}`);
      }

      // Update parent component's shipments data
      if (onShipmentsUpdate) {
        const updatedShipments = shipments.filter((s) => s.id !== shipment.id);
        onShipmentsUpdate(updatedShipments);
      }
    } catch (error) {
      console.error("Error deleting shipment:", error);
      toast.error(`Failed to delete shipment ${shipment.id}`);
    }
  };

  const handleChangeDeliveryType = async (shipment) => {
    try {
      // Format the date to YYYY-MM-DD
      const formattedDate =
        selectedDate instanceof Date
          ? selectedDate.toISOString().split("T")[0]
          : selectedDate;

      // Determine the new delivery type based on specific rules:
      // - If current type is NP -> change to Partner
      // - If current type is Partner -> change to NP
      // - Otherwise, don't change (but this should never happen since we only show the button on deliveries)
      const currentType = shipment.delivery_partner;
      let newType;
      
      if (currentType === "NP") {
        newType = "GLS";
      } else if (currentType === "GLS") {
        newType = "NP";
      } else if (currentType === "gls_pickup") {
        newType = "np_pickup";
      } else if (currentType === "np_pickup") {
        newType = "gls_pickup";
      } else {
        newType = currentType;
      }
      

      const result = await updateShipmentDeliveryType(shipment.number, newType);

      if (!result?.success) {
        throw new Error(`Failed to change delivery type: ${result?.error}`);
      }

      // Update the shipment locally to reflect the change immediately
      if (onShipmentsUpdate) {
        const updatedShipments = shipments.map((s) => {
          if (s.number === shipment.number) {
            return {
              ...s,
              delivery_partner: newType,
            };
          }
          return s;
        });

        onShipmentsUpdate(updatedShipments);
      }

      // Show success message
      toast.success(
        `Changed delivery type for shipment ${shipment.number} from ${currentType} to ${newType}`
      );
    } catch (error) {
      console.error("Error changing delivery type:", error);
      toast.error(`Failed to change delivery type for shipment ${shipment.id}`);
    }
  };

  const [isMapLoading, setIsMapLoading] = useState(true);
  const mapRef = useRef(null);
  const isLoadingRef = useRef(true); // Track loading state in a ref to avoid race conditions

  // Callback function to handle map ready events
  const handleMapReady = useCallback(() => {
    console.log("Map is ready, ending loading state");
    isLoadingRef.current = false;
    setIsMapLoading(false);
  }, []);

  // Force end loading after a timeout regardless of map creation
  useEffect(() => {
    console.log("Setting up map load timeout");
    const timer = setTimeout(() => {
      if (isLoadingRef.current) {
        console.log("Force ending loading state after timeout");
        isLoadingRef.current = false;
        setIsMapLoading(false);
      }
    }, 3000); // 3 seconds max loading time

    return () => clearTimeout(timer);
  }, []); // Run only once on mount

  return (
    <div style={containerStyle}>
      {isMapLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-700">Loading map data...</p>
          </div>
        </div>
      )}
      <MapContainer center={defaultCenter} zoom={8} style={containerStyle}>
        {/* Use the NoZoomUpdater instead of MapUpdater */}
        <NoZoomUpdater />

        {/* Use the MapReadyHandler to set loading state to false */}
        <MapReadyHandler onMapReady={handleMapReady} />
        
        {/* Provide map reference to parent component */}
        {onMapReady && <MapRefProvider onMapReady={onMapReady} />}

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Render delivery shipments */}
        {deliveryShipments.map((shipment) => (
          <MarkerWithPopup
            key={shipment.number}
            shipment={shipment}
            icon={createCustomIcon(shipment.delivery_partner)}
            isSelected={selectedShipment?.number === shipment.number}
            handlers={{
              handleDeleteShipment,
              handleChangeDeliveryType
            }}
          />
        ))}

        {/* Render self-pickup locations */}
        {selfPickupLocations.map((location, index) => (
          <Marker
            key={`depot-${index}`}
            position={[location.latitude, location.longitude]}
            icon={createCustomIcon("Self Pick Up")}
          >
            <Popup>
              <div>
                <strong>
                  Depot - {location.count} self-pickup
                  {location.count !== 1 ? "s" : ""}
                </strong>
                <br />
                {location.address}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default ShipmentsMap;
